import React, { useEffect, useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { SelectField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { ChevronIcon, ChevronIconDirection } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronIcon'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { ONE_OFF_FREQUENCY } from '../data/flexibleAssessmentsApi'

export type AddedAssessmentStatusAction = 'pause' | 'resume'

export type AddedAssessmentStatusModalProps = {
  action: AddedAssessmentStatusAction
  assessmentName: string
  clientName?: string
  frequency: number
  isLoading: boolean
  onConfirm: (frequency?: number) => void
  onRequestClose: () => void
  visible: boolean
}

const MESSAGES = defineMessages({
  close: {
    defaultMessage: 'Close',
    description: 'Accessibility label for the added-assessment status modal close button',
  },
  frequency: {
    defaultMessage: 'Frequency',
    description: 'Label for the resumed added-assessment frequency selector',
  },
  recurringFrequency: {
    defaultMessage: 'Every {frequency, plural, one {session} other {# sessions}}',
    description: 'Frequency option for a recurring added assessment',
  },
  sendOnceNow: {
    defaultMessage: 'Send once now',
    description: 'One-off frequency option when resuming an added assessment',
  },
  resumeTitle: {
    defaultMessage: 'Resume {assessment} assessment',
    description: 'Title for the resume added-assessment modal',
  },
  resume: {
    defaultMessage: 'Resume',
    description: 'Button that confirms resuming an added assessment',
  },
  pauseTitle: {
    defaultMessage: 'Pause recurring {assessment} assessments',
    description: 'Title for the pause added-assessment confirmation modal',
  },
  pauseDescription: {
    defaultMessage:
      'This will stop any future {assessment} assessments from being automatically sent to {clientName}. Any past assessment history will remain saved.',
    description: 'Description in the pause added-assessment confirmation modal',
  },
  clientFallback: {
    defaultMessage: 'the client',
    description: 'Fallback client name in the pause added-assessment confirmation modal',
  },
  cancel: {
    defaultMessage: 'Cancel',
    description: 'Button that cancels pausing an added assessment',
  },
  pause: {
    defaultMessage: 'Pause',
    description: 'Button that confirms pausing an added assessment',
  },
})

const STANDARD_FREQUENCIES = [ONE_OFF_FREQUENCY, 1, 2, 4]
const BASE_INPUT_STYLE = { marginBottom: 0 }

const ResumeContentContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  width: '494px',
  alignSelf: 'center',
  gap: spacing['16px'],
}))

const HeaderContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
})

const FieldContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['8px'],
}))

const ActionsContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginTop: spacing['16px'],
}))

const FrequencySelectField = styled(SelectField<number>)({})

export const AddedAssessmentStatusModal: React.FC<AddedAssessmentStatusModalProps> = ({
  action,
  assessmentName,
  clientName,
  frequency,
  isLoading,
  onConfirm,
  onRequestClose,
  visible,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const [selectedFrequency, setSelectedFrequency] = useState(frequency)

  useEffect(() => {
    if (visible) setSelectedFrequency(frequency)
  }, [frequency, visible])

  const frequencyOptions = useMemo(
    () =>
      [...new Set([...STANDARD_FREQUENCIES, frequency])]
        .filter((option) => Number.isInteger(option) && option >= ONE_OFF_FREQUENCY)
        .sort((left, right) => left - right)
        .map((option) => ({
          label:
            option === ONE_OFF_FREQUENCY
              ? formatMessage(MESSAGES.sendOnceNow)
              : formatMessage(MESSAGES.recurringFrequency, { frequency: option }),
          value: option,
        })),
    [formatMessage, frequency],
  )

  const hasValidFrequency =
    typeof selectedFrequency === 'number' &&
    Number.isInteger(selectedFrequency) &&
    selectedFrequency >= ONE_OFF_FREQUENCY
  const resumeTitle = formatMessage(MESSAGES.resumeTitle, { assessment: assessmentName })

  if (action === 'pause') {
    return (
      <ConfirmationModal
        visible={visible}
        width='600px'
        closeOnScrim={!isLoading}
        showCloseIcon={!isLoading}
        testID={tID('AddedAssessmentPauseModal')}
        modalTitle={formatMessage(MESSAGES.pauseTitle, { assessment: assessmentName })}
        modalContents={formatMessage(MESSAGES.pauseDescription, {
          assessment: assessmentName,
          clientName: clientName || formatMessage(MESSAGES.clientFallback),
        })}
        cancelButtonText={formatMessage(MESSAGES.cancel)}
        confirmationButtonText={formatMessage(MESSAGES.pause)}
        confirmButtonDisabled={isLoading}
        confirmButtonLoading={isLoading}
        cancelButtonDisabled={isLoading}
        onConfirmationButtonPress={() => onConfirm()}
        onRequestClose={onRequestClose}
      />
    )
  }

  return (
    <Modal
      modalContents={
        <ResumeContentContainer testID={tID('AddedAssessmentResumeModal')}>
          <HeaderContainer>
            <Title size={TitleSize.LARGE} text={resumeTitle} />
            {!isLoading && (
              <PressableOpacity
                onPress={onRequestClose}
                accessibilityLabel={formatMessage(MESSAGES.close)}
                testID={tID('AddedAssessmentResumeModal-close')}
              >
                <XIcon size={24} fillColor={colors.iconDefault} />
              </PressableOpacity>
            )}
          </HeaderContainer>
          <FieldContainer>
            <BodyText size={BodyTextSize.DEFAULT} bold text={formatMessage(MESSAGES.frequency)} />
            <FrequencySelectField
              value={selectedFrequency}
              name='resume-frequency'
              onChange={(value) => {
                const parsedFrequency = Number(value)
                if (Number.isInteger(parsedFrequency) && parsedFrequency >= ONE_OFF_FREQUENCY) {
                  setSelectedFrequency(parsedFrequency)
                }
              }}
              accessibilityLabel={formatMessage(MESSAGES.frequency)}
              options={frequencyOptions}
              disabled={isLoading}
              icon={<ChevronIcon fillColor={colors.iconDefault} direction={ChevronIconDirection.DOWN} size={16} />}
              baseInputStyle={BASE_INPUT_STYLE}
              disablePlaceholder
            />
          </FieldContainer>
          <ActionsContainer>
            <PrimaryButton
              disabled={!hasValidFrequency || isLoading}
              loading={isLoading}
              onPress={() => {
                if (hasValidFrequency) onConfirm(selectedFrequency)
              }}
              text={formatMessage(MESSAGES.resume)}
              testID={tID('AddedAssessmentResumeModal-confirm')}
            />
          </ActionsContainer>
        </ResumeContentContainer>
      }
      width='544px'
      scrollable={false}
      disableFullScreenModal
      disableBottomSheet
      visible={visible}
      onRequestClose={onRequestClose}
      closeOnScrim={!isLoading}
      accessibilityLabel={resumeTitle}
    />
  )
}
